import React from "react";
import { Loader2 } from "lucide-react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant;
    size?: Size;
    loading?: boolean;
    icon?: React.ElementType;
}

const variants: Record<Variant, string> = {
    primary:
        "bg-[#8b5cf6] hover:bg-[#7c3aed] text-white shadow-lg shadow-[#8b5cf6]/20 border border-transparent",
    secondary:
        "bg-slate-800/60 hover:bg-slate-800 text-slate-200 border border-slate-700",
    ghost:
        "bg-transparent hover:bg-slate-800/60 text-slate-300 hover:text-white border border-transparent",
    danger:
        "bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20",
};

const sizes: Record<Size, string> = {
    sm: "px-3 py-2 text-xs rounded-xl gap-1.5",
    md: "px-4 py-2.5 text-sm rounded-xl gap-2",
    lg: "px-6 py-3.5 text-sm rounded-2xl gap-2",
};

export default function Button({
    variant = "primary",
    size = "md",
    loading = false,
    icon: Icon,
    disabled,
    className = "",
    children,
    type = "button",
    ...rest
}: ButtonProps) {
    const iconSize = size === "sm" ? 14 : 16;

    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={`inline-flex items-center justify-center font-semibold transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 ${variants[variant]} ${sizes[size]} ${className}`}
            {...rest}
        >
            {loading ? <Loader2 size={iconSize} className="animate-spin" /> : Icon ? <Icon size={iconSize} /> : null}
            {children}
        </button>
    );
}

/** Small square icon-only button for table rows */
export function IconButton({
    icon: Icon,
    label,
    tone = "default",
    className = "",
    ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    icon: React.ElementType;
    label: string;
    tone?: "default" | "danger";
}) {
    const toneClass =
        tone === "danger"
            ? "hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20"
            : "hover:text-[#a78bfa] hover:bg-[#8b5cf6]/10 hover:border-[#8b5cf6]/20";

    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            className={`p-2 rounded-lg text-slate-400 border border-transparent transition-colors ${toneClass} ${className}`}
            {...rest}
        >
            <Icon size={16} />
        </button>
    );
}